import VueRouter from 'vue-router';

import auth from '../api/auth';
import login from './login/login.routes';
import dashboard from './dashboard/dashboard.routes';
import authRoute from './auth/auth.routes';
import formRoute from './forms/forms.routes';
import { frontendURL } from '../helper/URLHelper';
import PMConnectAPI from './dashboard/settings/bot/PMConnectAPI';
import AccountAPI from '../api/account';
import { SIGNUP_FLOW } from '../helper/demoAutomationHelper';
import store from '../store';

// add a public routes

const routes = [
  ...login.routes,
  ...dashboard.routes,
  ...authRoute.routes,
  ...formRoute.routes,
  {
    path: '/',
    redirect: '/app',
  },
];

window.roleWiseRoutes = {
  agent: [],
  administrator: [],
};

const getUserRole = ({ accounts } = {}, accountId) => {
  const currentAccount = accounts.find(account => account.id === accountId);
  return currentAccount ? currentAccount.role : null;
};

// generateRoleWiseRoute - updates window object with agent/admin route
const generateRoleWiseRoute = route => {
  route.forEach(element => {
    if (element.children) {
      generateRoleWiseRoute(element.children);
    }
    if (element.roles) {
      element.roles.forEach(roleEl => {
        window.roleWiseRoutes[roleEl].push(element.name);
      });
    }
  });
};
// Create a object of routes
// accessible by each role.
// returns an object with roles as keys and routeArr as values
generateRoleWiseRoute(routes);

export const router = new VueRouter({ mode: 'history', routes });

const publicForAllRoutes = [
  'account_public_form',
  // 'some_other_public_page',
];

const unProtectedRoutes = [
  'login',
  'auth_signup',
  'auth_reset_password',
  // 'account_public_form',
];

const authIgnoreRoutes = [
  'auth_confirmation',
  'pushBack',
  'auth_password_edit',
  // 'business_category',
];

function routeIsAccessibleFor(route, role) {
  return window.roleWiseRoutes[role].includes(route);
}

const routeValidators = [
  {
    protected: false,
    loggedIn: true,
    handler: () => {
      const user = auth.getCurrentUser();
      return `accounts/${user.account_id}/dashboard`;
    },
  },
  {
    protected: true,
    loggedIn: false,
    handler: () => 'login',
  },
  {
    protected: true,
    loggedIn: true,
    handler: to => {
      const user = auth.getCurrentUser();
      const userRole = getUserRole(user, Number(to.params.accountId));
      const isAccessible = routeIsAccessibleFor(to.name, userRole);
      return isAccessible ? null : `accounts/${to.params.accountId}/dashboard`;
    },
  },
  {
    protected: false,
    loggedIn: false,
    handler: () => null,
  },
];

export const validateAuthenticateRoutePermission = (to, from, next) => {
  const isLoggedIn = auth.isLoggedIn();
  const isProtectedRoute = !unProtectedRoutes.includes(to.name);
  const strategy = routeValidators.find(
    validator =>
      validator.protected === isProtectedRoute &&
      validator.loggedIn === isLoggedIn
  );
  const nextRoute = strategy.handler(to);
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

const validateRouteAccess = (to, from, next) => {
  if (
    window.peasyConfig.signupEnabled !== 'true' &&
    to.meta &&
    to.meta.requireSignupEnabled
  ) {
    const user = auth.getCurrentUser();
    next(frontendURL(`accounts/${user.account_id}/dashboard`));
  }

  if (authIgnoreRoutes.includes(to.name)) {
    return next();
  }
  return validateAuthenticateRoutePermission(to, from, next);
};

// Validate access based on restricted_modules from user plan
const validateRestrictedModulesAccess = async to => {
  const user = auth.getCurrentUser();
  if (!user || !user.account_id) {
    return { isAllowed: true };
  }

  // Get restricted_modules from Vuex store
  let userPlan = store?.state?.accounts?.userPlan;

  // If userPlan is not loaded yet, fetch it
  if (!userPlan || !userPlan.restricted_modules) {
    try {
      const response = await AccountAPI.fetchAccountInbox();
      userPlan = response?.data || {};
      // Update store
      if (store?.state?.accounts) {
        store.state.accounts.userPlan = userPlan;
      }
    } catch (error) {
      console.error('Error fetching user plan:', error);
      return { isAllowed: true };
    }
  }

  const restrictedModules = userPlan.restricted_modules || [];

  // If no restrictions, allow all
  if (restrictedModules.length === 0) {
    return { isAllowed: true };
  }

  // Map restricted modules to route path patterns
  const moduleToPathMap = {
    conversations: [
      '/conversations',
      '/inbox/',
      '/label/',
      '/team/',
      '/custom_view/',
      '/mentions',
    ],
    contacts: ['/contacts'],
    campaigns: ['/campaigns'],
    ai_agents: ['/ai'],
    orders: ['/catalog'],
    appointments: ['/calendar'],
    settings: ['/settings'],
  };

  // Always allowed routes
  const alwaysAllowedRoutes = [
    'goal_dashboard',
    'settings_account_reports',
    'documentation',
    'credit_home',
    'external_link',
  ];

  if (alwaysAllowedRoutes.includes(to.name)) {
    return { isAllowed: true };
  }

  // Check if current path matches any restricted module that is NOT in the user's allowed list
  const blockedModule = Object.keys(moduleToPathMap).find(module => {
    // Skip if module is allowed (in restricted_modules)
    if (restrictedModules.includes(module)) {
      return false;
    }

    // Check if path matches any pattern for this module
    const paths = moduleToPathMap[module];
    return paths.some(pathPattern => to.path.includes(pathPattern));
  });

  if (blockedModule) {
    return {
      isAllowed: false,
      redirectTo: `accounts/${user.account_id}/dashboard`,
    };
  }

  return { isAllowed: true };
};

// protecting routes
router.beforeEach(async (to, from, next) => {
  // console.log(to);

  // Allow access to public routes for both logged-in and non-logged-in users
  if (publicForAllRoutes.includes(to.name)) {
    return next(); // allow access
  }

  const currentUser = auth.getCurrentUser();
  let toAppend = '';

  // Check restricted_modules access (early check to prevent component render before redirect)
  const restrictedModulesAccess = await validateRestrictedModulesAccess(to);
  if (!restrictedModulesAccess.isAllowed) {
    return next(frontendURL(restrictedModulesAccess.redirectTo));
  }

  if (to.path === '/app/pm-auth-callback') {
    localStorage.setItem('pm-auth-token', to.query.auth);
    let genericTokenResponse = await PMConnectAPI.getGenericAcesssToken();
    if (genericTokenResponse?.key) {
      const user = auth.getCurrentUser();
      const payload = {
        email: user.email,
        account_id: user.pm_account_id,
        pm_access_token: genericTokenResponse?.key,
      };
      await AccountAPI.updateToken(payload);
    }

    window.close();
  }
  if (to.path === '/app/peasy-integrate-callback') {
    const { fb_access_token, pm_access_token } = to.query;

    const user = auth.getCurrentUser();

    let accountUpdateResponse = await AccountAPI.update(
      user.account_id,
      {
        fb_access_token: fb_access_token,
        pm_access_token: pm_access_token,
      },
      true
    );

    console.debug('accountUpdateResponse', accountUpdateResponse);

    // Redirect back to peasy marketing after saving fb_access_token and pm_access_token
    window.location = PMConnectAPI.getBaseURL();
  }
  if (to.path === '/app/external' && to.query.buy_now_plan_id) {
    toAppend = '?buy_now_plan_id=' + to.query.buy_now_plan_id;

    // add query params for grant user
    const {
      buy_now_plan_id,
      activation_email,
      activation_account_name,
      source,
    } = to.query;
    if (buy_now_plan_id && activation_email)
      toAppend += `&activation_email=${activation_email}`;
    if (buy_now_plan_id && activation_account_name)
      toAppend += `&activation_account_name=${activation_account_name}`;
    if (buy_now_plan_id && source) toAppend += `&source=${source}`;

    if (to.query.fpr) {
      toAppend = toAppend + '&fpr=' + to.query.fpr;
    }
  }

  // stripe successfull redirect with "?upgradeSuccessful=true", navigate to home
  if (
    to.query.upgradeSuccessful === 'true' &&
    to.name !== 'subscription_plans'
  ) {
    // add the query param upgradeSuccessful and from sunscription page keeep watch:{} on url if upgradeSuccessful=true navigate to home
    // it will get the render time to updatet the plan id 16 to 20 after stripe redirect
    console.debug('Upgrade successful!', { to, currentUser });

    return next(
      frontendURL(
        `accounts/${currentUser.account_id}/settings/subscription-plans?upgradeSuccessful=true`
      )
    );
  }

  if (!to.name) {
    const user = auth.getCurrentUser();
    if (user) {
      if (toAppend !== '') {
        return next(
          frontendURL(
            `accounts/${user.account_id}/settings/subscription-plans` + toAppend
          )
        );
      }
      return next(frontendURL(`accounts/${user.account_id}/dashboard`));
    }
    if (toAppend !== '') {
      return next('/app/auth/signup' + toAppend);
    }
    return next('/app/login' + toAppend);
  }

  // if first time signup, before subsctiption go to select and arrange business type
  if (to.name) {
    if (to.name === 'arrange_business_type') {
      return validateRouteAccess(to, from, next);
    }

    const user = auth.getCurrentUser();
    const signup_flow = SIGNUP_FLOW.getFlow();
    // if user is not verified, redirect to verification page(Business-typePage)
    // dont care from which flow is it, user.confirm
    const isNewUser =
      signup_flow?.isNewSignup &&
      signup_flow?.current_step === SIGNUP_FLOW.FLOWS.business_type;

    if ((isNewUser && user?.plan_id === 16) || (user && !user?.confirmed)) {
      // if user is on confirmation, allow to go there
      if (to.name === 'auth_confirmation') {
        return validateRouteAccess(to, from, next);
      }
      // strict user on this page
      return next(
        frontendURL(
          `accounts/${user?.account_id}/arrange/business-type`,
          to.query
        )
      );
    }

    if (
      user?.plan_id === 16 &&
      !user.sector &&
      signup_flow?.current_step !== SIGNUP_FLOW.FLOWS.select_plan
    ) {
      // for existing old users(newSignup=false),
      return next(
        frontendURL(
          `accounts/${user?.account_id}/arrange/business-type`,
          to.query
        )
      );
    }
  }

  // lock the user if plan_name=free, plan_id=16  user at subscription page
  if (to.name && to.name !== 'subscription_plans') {
    const user = auth.getCurrentUser();
    // console.log(to);
    if (user && user.plan_id === 16) {
      // console.log("LOCK Free user path",user,user.plan_id);
      return next(
        frontendURL(
          `accounts/${user.account_id}/settings/subscription-plans`,
          to.query
        )
      );
    }
  }

  // redirect the user to home if user.type !== "SuperAdmin" for credit routes
  if (to.path.includes('/credit/')) {
    const user = auth.getCurrentUser();
    if (user.type !== 'SuperAdmin') {
      return next(frontendURL(`accounts/${user.account_id}/dashboard`));
    }
  }

  return validateRouteAccess(to, from, next);
});

export default router;
